import { useState } from "react";

export function useApi() {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // ── 通常呼び出し（まとめて受け取る。書類生成などに使用） ──
  const call = async ({ system, messages, maxTokens = 1000 }) => {
    setLoading(true);
    setError(null);
    try {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(r => setTimeout(r, 2000 * attempt));
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ system, messages, maxTokens }),
        });
        if (res.status === 529 || res.status === 503) {
          lastErr = new Error("サーバーが混雑しています。しばらくお待ちください。");
          continue;
        }
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `APIエラー (${res.status})`);
        }
        const data = await res.json();
        const text = data.content?.find((b) => b.type === "text")?.text ?? "";
        return text || "返答を取得できませんでした。再度お試しください。";
      }
      throw lastErr || new Error("接続に失敗しました");
    } catch (err) {
      setError(err.message);
      return `⚠️ ${err.message}。しばらく経ってから再度お試しください。`;
    } finally {
      setLoading(false);
    }
  };

  // ── ストリーミング呼び出し（生成されたそばから表示。チャットの体感速度を上げる） ──
  // onToken(全文) が逐次呼ばれる。最終的に全文を返す。
  const callStream = async ({ system, messages, maxTokens = 600, onToken }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ system, messages, maxTokens, stream: true }),
      });

      // ストリーミング不可・エラー時は通常呼び出しにフォールバック
      if (!res.ok || !res.body) {
        const errData = await res.json().catch(() => ({}));
        if (res.status === 529 || res.status === 503) {
          return await call({ system, messages, maxTokens });
        }
        throw new Error(errData.error || `APIエラー (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop(); // 行が途中で切れている可能性があるので残す
        for (const line of lines) {
          const t = line.trim();
          if (!t.startsWith("data:")) continue;
          const data = t.slice(5).trim();
          if (!data || data === "[DONE]") continue;
          try {
            const ev = JSON.parse(data);
            if (ev.type === "content_block_delta" && ev.delta?.type === "text_delta") {
              full += ev.delta.text;
              onToken && onToken(full);
            }
          } catch {
            /* 不完全なJSONは無視 */
          }
        }
      }
      return full || "返答を取得できませんでした。再度お試しください。";
    } catch (err) {
      setError(err.message);
      return `⚠️ ${err.message}。しばらく経ってから再度お試しください。`;
    } finally {
      setLoading(false);
    }
  };

  return { call, callStream, loading, error };
}
